import { useNode, Element } from "@craftjs/core";
import React, { useRef } from "react";
import { Box, Typography } from "@mui/material";
import { useResponsive } from "../../hooks/useResponsive";
import { Text } from "./Text";
import { selectedOutlineSx, labelStyle, addButtonStyle, removeButtonStyle, inputStyle, itemCardStyle } from "../../config/settingsStyles";
import {
  TextField,
  TextareaField,
  ColorField,
  SliderField,
  CheckboxField,
  SettingsSection,
  SettingsRow,
} from "../ui/form/SettingsField";

export interface ServiceItem {
  name: string;
  description?: string;
  credit?: string;
  baht?: string;
  note?: string;
}

export interface PricingCardProps {
  planName: string;
  credit: string;
  price: string;
  unit: string;
  services: ServiceItem[];
  note?: string;
  badgeColor: string;
  headerBackground: string;
  bahtHeaderBg: string;
  checkColor: string;
  headerTextColor: string;
  backgroundColor: string;
  textColor: string;
  borderRadius: number;
  showShadow: boolean;
  textFontFamily?: string;
  // Legacy
  features?: string[];
}

// Stroke checkmark SVG matching humbleicons:check from Figma
const StrokeCheck = ({ color, size = 20 }: { color: string; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

export const PricingCard = ({
  planName = "\u0E41\u0E1E\u0E47\u0E01\u0E40\u0E25\u0E47\u0E01\u0E2A\u0E38\u0E14\u0E04\u0E38\u0E49\u0E21",
  credit = "600",
  price = "300",
  unit = "\u0E3F/\u0E04\u0E23\u0E31\u0E49\u0E07",
  services = [],
  features,
  note,
  badgeColor = "linear-gradient(180deg, #F2819F 0%, #FAAF40 100%)",
  headerBackground = "linear-gradient(116.39deg, #FFEBCF 2.25%, #EFFFF3 55.82%, #FFF7EE 100%)",
  bahtHeaderBg = "linear-gradient(90deg, #FFECCF 0%, #DAFFDC 100%)",
  checkColor = "#00B97E",
  headerTextColor = "#2C2C2B",
  backgroundColor = "#ffffff",
  textColor = "#2C2C2B",
  borderRadius = 20,
  showShadow = true,
  textFontFamily,
}: PricingCardProps) => {
  const {
    connectors: { connect, drag },
    selected,
    actions: { setProp },
  } = useNode((state) => ({
    selected: state.events.selected,
  }));

  const containerRef = useRef<HTMLDivElement>(null);
  const { isMobile } = useResponsive(containerRef as React.RefObject<HTMLElement>);

  // Legacy: convert old features[] to services[]
  const resolvedServices: ServiceItem[] =
    services && services.length > 0
      ? services
      : (features || []).map((f) => ({ name: f }));

  // Split unit: "฿/ครั้ง" → currency "฿" (large) + suffix "/ครั้ง" (small)
  const slashIdx = unit.indexOf("/");
  const currencyPart = slashIdx > 0 ? ` ${unit.substring(0, slashIdx)}` : "";
  const unitSuffix = slashIdx > 0 ? unit.substring(slashIdx) : unit;

  const padding = isMobile ? "12px" : "16px";
  const creditColWidth = isMobile ? 45 : 55;
  const bahtColWidth = isMobile ? 55 : 65;
  const colGap = 6;

  return (
    <Box
      ref={(ref: HTMLDivElement | null) => {
        if (ref) {
          connect(drag(ref));
          (containerRef as React.MutableRefObject<HTMLDivElement | null>).current = ref;
        }
      }}
      sx={{
        position: "relative",
        borderRadius: `${borderRadius}px`,
        overflow: "hidden",
        bgcolor: backgroundColor,
        boxShadow: showShadow ? "0px 0px 20px 5px rgba(0, 0, 0, 0.1)" : "none",
        width: "100%",
        maxWidth: 320,
        ...(selected && selectedOutlineSx),
      }}
    >
      {/* ─── Header gradient area (inset) ─── */}
      <Box
        sx={{
          mx: isMobile ? "8px" : "14px",
          mt: isMobile ? "8px" : "14px",
          background: headerBackground,
          borderRadius: `${borderRadius}px`,
          p: isMobile ? "10px 12px 12px" : "10px 16px 14px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          minHeight: isMobile ? 70 : 90,
        }}
      >
        {/* Plan name badge — white pill */}
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            bgcolor: "rgba(255, 255, 255, 0.8)",
            borderRadius: "20px",
            px: 1.2,
            py: 0.3,
            alignSelf: "flex-start",
          }}
        >
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              background: badgeColor,
              flexShrink: 0,
            }}
          />
          <Element
            id="pricing-plan"
            is={Text}
            text={planName}
            fontSize={14}
            fontWeight="normal"
            tagName="span"
            color="#000000"
            fontFamily={textFontFamily}
          />
        </Box>

        {/* Credits — bottom right */}
        <Box
          sx={{
            alignSelf: "flex-end",
            display: "flex",
            alignItems: "baseline",
            gap: "4px",
            mt: 0.5,
          }}
        >
          <Element
            id="pricing-credit"
            is={Text}
            text={String(credit)}
            fontSize={32}
            fontWeight="bold"
            tagName="span"
            color={headerTextColor}
            fontFamily={textFontFamily}
          />
          <Element
            id="pricing-credit-label"
            is={Text}
            text="Credits"
            fontSize={16}
            fontWeight="normal"
            tagName="span"
            color={headerTextColor}
            fontFamily="Fahkwang"
          />
        </Box>
      </Box>

      {/* ─── Price section ─── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          px: isMobile ? "20px" : "30px",
          pt: isMobile ? "10px" : "14px",
          pb: isMobile ? "8px" : "10px",
        }}
      >
        <Element
          id="pricing-price-label"
          is={Text}
          text={"\u0E23\u0E32\u0E04\u0E32"}
          fontSize={18}
          fontWeight="normal"
          tagName="span"
          color={textColor}
          fontFamily={textFontFamily}
        />
        <Box sx={{ display: "flex", alignItems: "baseline" }}>
          <Element
            id="pricing-price"
            is={Text}
            text={String(price)}
            fontSize={48}
            fontWeight="bold"
            tagName="span"
            color={textColor}
            fontFamily={textFontFamily}
          />
          <Element
            id="pricing-unit"
            is={Text}
            text={unitSuffix}
            fontSize={18}
            fontWeight="normal"
            tagName="span"
            color={textColor}
            fontFamily={textFontFamily}
          />
        </Box>
      </Box>

      {/* ─── Service Table ─── */}
      {resolvedServices.length > 0 && (
        <Box sx={{ pb: isMobile ? "10px" : "16px" }}>
          {/* Table Header — dark pill + gradient pill */}
          <Box sx={{ display: "flex", mx: padding, gap: `${colGap}px`, mb: "4px" }}>
            {/* Dark pill: Service + Credit */}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                bgcolor: "#404040",
                borderRadius: "30px",
                height: isMobile ? 22 : 24,
                overflow: "hidden",
              }}
            >
              <Box sx={{ flex: 1, pl: 1.5 }}>
                <Element id="pricing-header-service" is={Text} text="Service" fontSize={12} fontWeight="normal" tagName="span" color="#ffffff" fontFamily={textFontFamily} />
              </Box>
              <Box sx={{ width: creditColWidth, textAlign: "center" }}>
                <Element id="pricing-header-credit" is={Text} text="Credit" fontSize={12} fontWeight="normal" tagName="span" color="#ffffff" fontFamily={textFontFamily} />
              </Box>
            </Box>
            {/* Gradient pill: Baht */}
            <Box
              sx={{
                width: bahtColWidth,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: bahtHeaderBg,
                borderRadius: "30px",
                height: isMobile ? 22 : 24,
                flexShrink: 0,
              }}
            >
              <Element id="pricing-header-baht" is={Text} text="Baht" fontSize={12} fontWeight="normal" tagName="span" color={textColor} fontFamily={textFontFamily} />
            </Box>
          </Box>

          {/* Table Rows */}
          {resolvedServices.map((service, index) => {
            const hasValues = !!(service.credit || service.baht);
            const isLongText = !service.credit && service.baht && service.baht.length > 6;

            return (
              <Box key={index}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    px: padding,
                    py: "3px",
                    minHeight: isMobile ? 22 : 26,
                  }}
                >
                  {/* Service name + description */}
                  <Box sx={{ flex: 1, minWidth: 0, pl: 0.5 }}>
                    <Element
                      id={`pricing-svc-name-${index}`}
                      is={Text}
                      text={service.name}
                      fontSize={13}
                      fontWeight="normal"
                      tagName="span"
                      color="#000000"
                      fontFamily={textFontFamily}
                    />
                    {(service.description || service.note) && (
                      <Element
                        id={`pricing-svc-desc-${index}`}
                        is={Text}
                        text={[service.description, service.note].filter(Boolean).join("\n")}
                        fontSize={10}
                        fontWeight="normal"
                        tagName="span"
                        color="#000000"
                        fontFamily={textFontFamily}
                        whiteSpace="pre-line"
                      />
                    )}
                  </Box>

                  {/* Credit + Baht columns */}
                  {isLongText ? (
                    /* Long text spanning credit+gap+baht area */
                    <Box sx={{ width: creditColWidth + colGap + bahtColWidth, flexShrink: 0, "& *": { maxWidth: "100%", boxSizing: "border-box" } }}>
                      <Element id={`pricing-svc-baht-long-${index}`} is={Text} text={String(service.baht || "")} fontSize={11} fontWeight="normal" tagName="span" color="#000000" fontFamily={textFontFamily} textAlign="right" lineHeight={1.3} />
                    </Box>
                  ) : hasValues ? (
                    <>
                      <Box sx={{ width: creditColWidth, textAlign: "center", flexShrink: 0 }}>
                        <Element id={`pricing-svc-credit-${index}`} is={Text} text={String(service.credit || "")} fontSize={14} fontWeight="normal" tagName="span" color="#000000" fontFamily={textFontFamily} />
                      </Box>
                      <Box sx={{ width: colGap, flexShrink: 0 }} />
                      <Box sx={{ width: bahtColWidth, textAlign: "center", flexShrink: 0 }}>
                        <Element id={`pricing-svc-baht-${index}`} is={Text} text={String(service.baht || "")} fontSize={14} fontWeight="normal" tagName="span" color="#000000" fontFamily={textFontFamily} />
                      </Box>
                    </>
                  ) : (
                    /* Checkmarks in both columns (green) */
                    <>
                      <Box
                        sx={{
                          width: creditColWidth,
                          display: "flex",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <StrokeCheck color={checkColor} size={isMobile ? 14 : 16} />
                      </Box>
                      <Box sx={{ width: colGap, flexShrink: 0 }} />
                      <Box
                        sx={{
                          width: bahtColWidth,
                          display: "flex",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <StrokeCheck color={checkColor} size={isMobile ? 14 : 16} />
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Note */}
      <Box sx={{ px: padding, pb: note ? "12px" : 0 }}>
        <Element
          id="pricing-note"
          is={Text}
          text={note || ""}
          fontSize={11}
          fontWeight="normal"
          tagName="p"
          color={textColor}
          fontFamily={textFontFamily}
        />
      </Box>
    </Box>
  );
};

// =============================================================================
// Settings
// =============================================================================

export const PricingCardSettings = () => {
  const {
    actions: { setProp },
    unit,
    services,
    note,
    badgeColor,
    headerBackground,
    bahtHeaderBg,
    checkColor,
    headerTextColor,
    backgroundColor,
    textColor,
    borderRadius,
    showShadow,
  } = useNode((node) => ({
    unit: node.data.props.unit,
    services: (node.data.props.services || []) as ServiceItem[],
    note: node.data.props.note,
    badgeColor: node.data.props.badgeColor,
    headerBackground: node.data.props.headerBackground,
    bahtHeaderBg: node.data.props.bahtHeaderBg || "linear-gradient(90deg, #FFECCF 0%, #DAFFDC 100%)",
    checkColor: node.data.props.checkColor || "#00B97E",
    headerTextColor: node.data.props.headerTextColor,
    backgroundColor: node.data.props.backgroundColor,
    textColor: node.data.props.textColor,
    borderRadius: node.data.props.borderRadius,
    showShadow: node.data.props.showShadow,
  }));

  const updateService = (index: number, field: keyof ServiceItem, value: string) => {
    setProp((props: PricingCardProps) => {
      const newServices = [...props.services];
      newServices[index] = { ...newServices[index], [field]: value || undefined };
      props.services = newServices;
    });
  };

  const addService = () => {
    setProp((props: PricingCardProps) => {
      props.services = [...props.services, { name: "New Service" }];
    });
  };

  const removeService = (index: number) => {
    setProp((props: PricingCardProps) => {
      props.services = props.services.filter((_, i) => i !== index);
    });
  };

  return (
    <SettingsSection>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
        💡 คลิกที่ตัวอักษรบน canvas เพื่อแก้ไข
      </Typography>

      <TextField
        label="Unit"
        value={unit}
        onChange={(v) => setProp((props: PricingCardProps) => (props.unit = v))}
      />

      {/* Services Table */}
      <div>
        <label style={labelStyle}>Services</label>
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {services.map((service: ServiceItem, index: number) => (
            <div key={index} style={{ ...itemCardStyle, position: "relative" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "6px" }}>
                <input
                  type="text"
                  value={service.name}
                  onChange={(e) => updateService(index, "name", e.target.value)}
                  style={{ ...inputStyle, flex: 1, fontWeight: 600 }}
                  placeholder="Service name"
                />
                <button onClick={() => removeService(index)} style={removeButtonStyle}>
                  {"\u00D7"}
                </button>
              </div>
              <textarea
                value={service.description || ""}
                onChange={(e) => updateService(index, "description", e.target.value)}
                style={{ ...inputStyle, fontSize: "0.75rem", marginBottom: "4px", resize: "vertical", minHeight: "28px", fontFamily: "inherit" }}
                placeholder="Description (optional)"
                rows={1}
              />
              <div style={{ display: "flex", gap: "4px" }}>
                <input
                  type="text"
                  value={service.credit || ""}
                  onChange={(e) => updateService(index, "credit", e.target.value)}
                  style={{ ...inputStyle, flex: 1 }}
                  placeholder="Credit"
                />
                <input
                  type="text"
                  value={service.baht || ""}
                  onChange={(e) => updateService(index, "baht", e.target.value)}
                  style={{ ...inputStyle, flex: 1 }}
                  placeholder="Baht"
                />
              </div>
              <input
                type="text"
                value={service.note || ""}
                onChange={(e) => updateService(index, "note", e.target.value)}
                style={{ ...inputStyle, fontSize: "0.75rem", marginTop: "4px" }}
                placeholder="Note (optional)"
              />
            </div>
          ))}
        </div>
        <button onClick={addService} style={addButtonStyle}>
          + Add Service
        </button>
      </div>

      <TextareaField
        label="Note (optional)"
        value={note || ""}
        onChange={(v) => setProp((props: PricingCardProps) => (props.note = v || undefined))}
        rows={2}
        placeholder="e.g. \u0E2B\u0E21\u0E32\u0E22\u0E40\u0E2B\u0E15\u0E38: \u0E23\u0E32\u0E04\u0E32\u0E44\u0E21\u0E48\u0E23\u0E27\u0E21\u0E20\u0E32\u0E29\u0E35\u0E21\u0E39\u0E25\u0E04\u0E48\u0E32\u0E40\u0E1E\u0E34\u0E48\u0E21"
      />

      {/* Gradient fields — use text input for CSS gradient support */}
      <TextField
        label="Header Gradient"
        value={headerBackground}
        onChange={(v) => setProp((props: PricingCardProps) => (props.headerBackground = v))}
      />

      <TextField
        label="Badge Color/Gradient"
        value={badgeColor}
        onChange={(v) => setProp((props: PricingCardProps) => (props.badgeColor = v))}
      />

      <TextField
        label="Baht Header Gradient"
        value={bahtHeaderBg}
        onChange={(v) => setProp((props: PricingCardProps) => (props.bahtHeaderBg = v))}
      />

      <ColorField
        label="Check Color"
        value={checkColor}
        onChange={(v) => setProp((props: PricingCardProps) => (props.checkColor = v))}
      />

      <SettingsRow>
        <ColorField
          label="Header Text"
          value={headerTextColor}
          onChange={(v) => setProp((props: PricingCardProps) => (props.headerTextColor = v))}
        />
        <ColorField
          label="Background"
          value={backgroundColor}
          onChange={(v) => setProp((props: PricingCardProps) => (props.backgroundColor = v))}
        />
      </SettingsRow>

      <ColorField
        label="Text Color"
        value={textColor}
        onChange={(v) => setProp((props: PricingCardProps) => (props.textColor = v))}
      />

      <SliderField
        label="Border Radius"
        value={borderRadius}
        onChange={(v) => setProp((props: PricingCardProps) => (props.borderRadius = v))}
        min={0}
        max={32}
        unit="px"
      />

      <CheckboxField
        label="Show Shadow"
        checked={showShadow}
        onChange={(v) => setProp((props: PricingCardProps) => (props.showShadow = v))}
      />
    </SettingsSection>
  );
};

// =============================================================================
// Craft Config
// =============================================================================

PricingCard.craft = {
  displayName: "Pricing Card",
  isCanvas: true,
  props: {
    planName: "\u0E41\u0E1E\u0E47\u0E01\u0E40\u0E25\u0E47\u0E01\u0E2A\u0E38\u0E14\u0E04\u0E38\u0E49\u0E21",
    credit: "600",
    price: "300",
    unit: "\u0E3F/\u0E04\u0E23\u0E31\u0E49\u0E07",
    services: [
      { name: "Service A", credit: "10", baht: "5.00" },
      { name: "Service B", credit: "20", baht: "10.00" },
      { name: "Service C" },
      { name: "Service D" },
    ],
    note: undefined,
    badgeColor: "linear-gradient(180deg, #F2819F 0%, #FAAF40 100%)",
    headerBackground: "linear-gradient(116.39deg, #FFEBCF 2.25%, #EFFFF3 55.82%, #FFF7EE 100%)",
    bahtHeaderBg: "linear-gradient(90deg, #FFECCF 0%, #DAFFDC 100%)",
    checkColor: "#00B97E",
    headerTextColor: "#2C2C2B",
    backgroundColor: "#ffffff",
    textColor: "#2C2C2B",
    borderRadius: 20,
    showShadow: true,
    textFontFamily: undefined,
  },
  rules: {
    canMoveIn: () => false,
    canMoveOut: () => false,
  },
  related: {
    settings: PricingCardSettings,
  },
};
